import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Cocktails, type CocktailMatch, type ExternalCocktailRecipe } from '@/lib/api/v1';
import { Badge, Card, EmptyState, Icon, Input, Skeleton } from '@/design-system';

/**
 * Bar — the cocktail half of the brand promise.
 *
 * Three tabs:
 *   - Hva kan jeg lage nå (canMake): recipes with all required roles in stock
 *   - Nesten der (almost): missing 1-2 ingredients — drives wishlist/buy intent
 *   - Alle (all): full browseable library
 *
 * Recipes are matched server-side against the user's bottles + pantry
 * defaults. Inference is name-keyword first, category fallback.
 */

type Filter = 'can-make' | 'almost' | 'all';

const FILTERS: { id: Filter; label: string; icon: 'check-circle' | 'sparkles' | 'list' }[] = [
  { id: 'can-make', label: 'Kan lage nå', icon: 'check-circle' },
  { id: 'almost', label: 'Nesten der', icon: 'sparkles' },
  { id: 'all', label: 'Alle', icon: 'list' },
];

const BASE_PILLS: { value: string; label: string }[] = [
  { value: '', label: 'Alle baser' },
  { value: 'gin', label: 'Gin' },
  { value: 'vodka', label: 'Vodka' },
  { value: 'whiskey', label: 'Whiskey' },
  { value: 'rum', label: 'Rom' },
  { value: 'tequila', label: 'Tequila' },
  { value: 'wine', label: 'Vin/musserende' },
];

export function BarPage() {
  const [filter, setFilter] = useState<Filter>('can-make');
  const [base, setBase] = useState<string>('');
  const [externalQuery, setExternalQuery] = useState('');
  const externalTerm = externalQuery.trim();

  const cocktailsQ = useQuery({
    queryKey: ['cocktails', filter, base],
    queryFn: () =>
      Cocktails.list({
        filter,
        base: base || undefined,
      }),
    staleTime: 5 * 60_000,
  });

  const externalQ = useQuery({
    queryKey: ['cocktails.external', externalTerm],
    queryFn: () => Cocktails.searchExternal(externalTerm, 8),
    enabled: externalTerm.length >= 2,
    staleTime: 10 * 60_000,
  });

  const cocktails = cocktailsQ.data?.cocktails ?? [];
  const summary = cocktailsQ.data?.summary;

  return (
    <div className="grid gap-6">
      <header className="grid gap-2">
        <h1 className="ui-section-title text-3xl">Baren</h1>
        <p className="text-sm text-mist">
          AI-en finner cocktailene du kan lage akkurat nå — og hva du mangler for å lage flere.
        </p>
        {summary && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <Badge tone="gold">{summary.canMake} klare</Badge>
            <Badge tone="ruby">{summary.almost} nesten</Badge>
            <Badge tone="mist">{summary.total} totalt</Badge>
            <span className="text-[10px] uppercase tracking-widest text-mist">
              · {summary.availableRoles.length} ingredienser i baren
            </span>
          </div>
        )}
      </header>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={`ui-pill ${filter === f.id ? 'ui-pill--active' : ''}`}
          >
            <Icon name={f.icon} size={12} />
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        {BASE_PILLS.map((b) => (
          <button
            key={b.value}
            type="button"
            onClick={() => setBase(b.value)}
            className={`ui-pill ${base === b.value ? 'ui-pill--active' : ''}`}
          >
            {b.label}
          </button>
        ))}
      </div>

      <section className="grid gap-3">
        <div className="grid gap-1">
          <h2 className="ui-section-title text-2xl">Eksterne oppskrifter</h2>
          <p className="text-xs text-mist">
            Backup-søk for klassiske og internasjonale cocktails som ikke ligger i Alkisen-biblioteket.
          </p>
        </div>
        <Input
          iconLeft="search"
          label="Søk"
          placeholder="Margarita, Negroni, Daiquiri..."
          value={externalQuery}
          onChange={(event) => setExternalQuery(event.target.value)}
        />
        {externalTerm.length >= 2 && externalQ.isLoading && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="grid gap-3 p-3">
                <Skeleton height={96} />
                <Skeleton height={18} width="70%" />
                <Skeleton height={12} width="90%" />
              </Card>
            ))}
          </div>
        )}
        {externalTerm.length >= 2 && externalQ.data && (
          <ExternalCocktailResults cocktails={externalQ.data.cocktails} />
        )}
      </section>

      {cocktailsQ.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="grid gap-3 p-4">
              <Skeleton height={20} width="80%" />
              <Skeleton height={14} width="60%" />
              <Skeleton height={60} />
            </Card>
          ))}
        </div>
      ) : cocktails.length === 0 ? (
        <EmptyState
          icon="sparkles"
          title={
            filter === 'can-make'
              ? 'Ingen cocktails klare enda'
              : filter === 'almost'
                ? 'Ingen som er bare en flaske unna'
                : 'Ingen cocktails matchet filteret'
          }
          description={
            filter === 'can-make'
              ? 'Legg til en gin, en bourbon, eller en vermut i kjelleren — og se hva som dukker opp.'
              : 'Bytt base eller se alle cocktails for inspirasjon.'
          }
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cocktails.map((c) => (
            <CocktailCard key={c.recipe.slug} match={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function ExternalCocktailResults({ cocktails }: { cocktails: ExternalCocktailRecipe[] }) {
  if (cocktails.length === 0) {
    return (
      <EmptyState
        icon="search"
        title="Ingen eksterne treff"
        description="Prøv et annet cocktailnavn."
      />
    );
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {cocktails.map((cocktail) => (
        <article key={cocktail.id} className="grid overflow-hidden rounded-md border border-line bg-ink-soft/50">
          {cocktail.thumbnailUrl && (
            <img
              src={cocktail.thumbnailUrl}
              alt={cocktail.name}
              className="aspect-[4/3] w-full object-cover"
              loading="lazy"
            />
          )}
          <div className="grid gap-2 p-3">
            <div className="flex items-start gap-2">
              <h3 className="font-serif text-base leading-tight text-parchment">{cocktail.name}</h3>
              {cocktail.alcoholic && <Badge tone="mist" className="ml-auto shrink-0">{cocktail.alcoholic}</Badge>}
            </div>
            <div className="flex flex-wrap gap-1">
              {cocktail.category && <Badge tone="mist">{cocktail.category}</Badge>}
              {cocktail.glass && <Badge tone="gold">{cocktail.glass}</Badge>}
            </div>
            {cocktail.ingredients.length > 0 && (
              <p className="text-[11px] text-mist line-clamp-2">
                {cocktail.ingredients
                  .slice(0, 5)
                  .map((item) => [item.measure, item.name].filter(Boolean).join(' '))
                  .join(', ')}
              </p>
            )}
            {cocktail.instructions && (
              <p className="text-xs text-parchment-dim line-clamp-3">{cocktail.instructions}</p>
            )}
          </div>
        </article>
      ))}
    </div>
  );
}

function CocktailCard({ match }: { match: CocktailMatch }) {
  const r = match.recipe;
  return (
    <Link
      to={`/bar/${r.slug}`}
      className="ui-card ui-card--interactive grid gap-3 p-4"
    >
      <div className="flex items-start gap-2">
        <h3 className="font-serif text-lg leading-tight text-parchment flex-1">{r.name}</h3>
        {match.canMake ? (
          <Badge tone="gold" className="shrink-0">
            <Icon name="check-circle" size={10} className="mr-1 inline" /> klar
          </Badge>
        ) : match.missingCount === 1 ? (
          <Badge tone="ruby" className="shrink-0">−1</Badge>
        ) : (
          <Badge tone="mist" className="shrink-0">−{match.missingCount}</Badge>
        )}
      </div>
      <p className="text-xs text-mist line-clamp-2">{r.description}</p>
      <div className="flex flex-wrap gap-1">
        <Badge tone="mist">{r.base}</Badge>
        {r.ibaClassic && <Badge tone="gold">IBA</Badge>}
        {r.tags.slice(0, 2).map((t) => (
          <Badge key={t} tone="mist">{t}</Badge>
        ))}
      </div>
      {!match.canMake && match.gaps.length > 0 && (
        <div className="border-t border-line pt-2">
          <span className="text-[10px] uppercase tracking-widest text-mist">Mangler:</span>
          <div className="mt-1 flex flex-wrap gap-1">
            {match.gaps.slice(0, 4).map((g) => (
              <span
                key={g.role}
                className="rounded-full bg-ink-soft px-2 py-0.5 text-[10px] text-parchment-dim"
              >
                {g.display}
              </span>
            ))}
          </div>
        </div>
      )}
    </Link>
  );
}
